/**
 * One drone list, out of three registries that have never agreed on what a
 * drone is.
 *
 *   /devices           registered hardware — serial_number, model, manufacturer
 *   /list_drones       the web-pilot fleet — drone_serial_number, drone_name
 *   /propulsion/drones a configuration catalog — frame, motors, AUW, no serial
 *
 * The first two identify an individual aircraft; the third identifies a *build*
 * and can be flown by many. They are kept as separate `source`s rather than
 * flattened, because an association to "this airframe" and one to "this
 * configuration" are different claims and only the first is a tail number.
 *
 * Ids are only unique within a source, so `ref` — "<source>:<id>" — is what
 * gets persisted. A bare uuid could not say which registry it came from.
 *
 * ponytail: merged in the client because there is no master catalog to merge
 * them in. Delete this file the day the backend serves one — the shape below is
 * the shape to serve, and `ref` is the column to store.
 */

/** Source order = trust order: an airframe beats a build when both match. */
export const SOURCES = ["fleet", "devices", "propulsion"];

const clean = (serial) => (serial || "").toString().toUpperCase().replace(/[^A-Z0-9]/g, "") || null;

export const normalizeFleetDrone = (d) => ({
  ref: `fleet:${d.id ?? d.drone_id}`,
  source: "fleet",
  id: d.id ?? d.drone_id,
  name: d.drone_name || d.drone_serial_number || "Unnamed drone",
  serial: clean(d.drone_serial_number),
  detail: [d.drone_type, d.drone_status].filter(Boolean).join(" · ") || null,
});

export const normalizeDevice = (d) => ({
  ref: `devices:${d.id ?? d.device_id}`,
  source: "devices",
  id: d.id ?? d.device_id,
  name: d.display_name || d.model || d.serial_number || "Unnamed device",
  serial: clean(d.serial_number),
  detail: [d.manufacturer, d.model].filter(Boolean).join(" ") || null,
});

export const normalizePropulsionDrone = (d) => ({
  ref: `propulsion:${d.id}`,
  source: "propulsion",
  id: d.id,
  name: [d.brand, d.name].filter(Boolean).join(" ") || "Unnamed build",
  serial: null, // a configuration, not an airframe — nothing to match on
  detail: [d.num_motors && `${d.num_motors} motors`, d.frame_size_mm && `${d.frame_size_mm}mm`, d.auw_g && `${d.auw_g}g`]
    .filter(Boolean)
    .join(" · ") || null,
});

/**
 * Merge, then drop the duplicates that are the same physical aircraft twice.
 *
 * A drone registered in the fleet AND in /devices shares a serial; listing it
 * twice makes the picker a quiz. Source order decides which copy survives.
 * Entries without a serial (every propulsion build) can never collide.
 */
export const mergeCatalog = (entries) => {
  const bySerial = new Map();
  const merged = [];
  for (const source of SOURCES) {
    for (const entry of entries.filter((e) => e.source === source)) {
      if (entry.serial && bySerial.has(entry.serial)) continue;
      if (entry.serial) bySerial.set(entry.serial, entry);
      merged.push(entry);
    }
  }
  return merged;
};

/**
 * Catalog entries whose serial IS the flight controller's uid.
 *
 * Nothing records an FC uid today, so this finds nothing until someone
 * registers a drone under one — which is exactly the bridge that is missing:
 * the log knows the board, the registries know a human-assigned serial, and no
 * column joins them.
 */
export const suggestFromHardware = (catalog, hw) => {
  const uid = clean(hw?.uid);
  return uid ? catalog.filter((e) => e.serial === uid) : [];
};
